import React, { useCallback, useEffect, useRef, useState } from "react";
import Select, { type Theme } from "react-select";
import {
  useNavigate,
  useOutletContext,
  useParams,
  useLocation,
} from "react-router";
import { toast } from "react-toastify";
import {
  ICmsQuestionType,
  ICmsSubject,
  ITeacherPassageDetail,
  ITeacherQuestionDetail,
  createTeacherQuestion,
  getCmsQuestionTypes,
  getCmsTeacherSubjects,
  getTeacherPassageById,
  getTeacherQuestionById,
  getTeacherQuestionTypePayload,
  saveTeacherQuestion,
} from "../../../../api/crmContent.service";
import { getAllDomainsBySubject } from "../../../../api/domain.service";
import { getStandardOptionsByDomain } from "../../../../api/standard.service";
import { getStateAll } from "../../../../api/subject.service";
import { getRequest } from "../../../../api/user.service";
import { initRequest } from "../../../shared/common/learnosityAuthorInit";
import { ISelectElement } from "../../../../model/interface/keyValuePair";

import { dokOptions, IDokOption } from "../../../../utils/dokOptions";
import constant from "../../../../utils/constant";
import { DeleteIcon } from "../../../../assets/icons/index";
import { IUserContentOutletContext } from "./UserContentLayout";
import {
  buildDuplicateQuestionRequest,
  canPreviewTeacherItem,
  getTeacherItemRef,
  numberOfPartsOptions,
  parsePassageIds,
} from "./questionHelpers";
import PreviewModal from "../../questions/previewModal";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import store from "../../../../redux/store";

interface IRouteState {
  numberOfParts?: number;
  passageIds?: number[];
}

const getPartFeatureRef = (
  teacherQuestionId: number,
  partIndex: number
): string =>
  `${teacherQuestionId}-q-${String(partIndex + 1).padStart(2, "0")}-teacher`;

const publishedLabel = (current: boolean): string => (current ? "Yes" : "No");

const reactSelectTheme = (theme: Theme): Theme => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary25: "#B3B3B3",
    primary: "#041E42",
  },
});

export const QuestionEditor: React.FC = () => {
  const { teacherQuestionId: teacherQuestionIdParam } = useParams<{
    teacherQuestionId: string;
  }>();
  const teacherQuestionId = Number(teacherQuestionIdParam);
  const location = useLocation();
  const routeState = (location.state ?? {}) as IRouteState;
  const navigate = useNavigate();
  const { userId, userInfo } = useOutletContext<IUserContentOutletContext>();

  const [teacherQuestion, setTeacherQuestion] =
    useState<ITeacherQuestionDetail | null>(null);
  const [passageIds, setPassageIds] = useState<number[]>([]);
  const [passageDetails, setPassageDetails] = useState<ITeacherPassageDetail[]>(
    []
  );
  const [currentPartIndex, setCurrentPartIndex] = useState<number>(0);
  const [numberOfParts, setNumberOfParts] = useState<number>(1);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [widgetDataArray, setWidgetDataArray] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isLoadingEditor, setIsLoadingEditor] = useState<boolean>(true);
  const [isEditingPassages, setIsEditingPassages] = useState<boolean>(false);
  const [passageIdsInput, setPassageIdsInput] = useState<string>("");
  const [isValidatingPassages, setIsValidatingPassages] =
    useState<boolean>(false);
  const [currentPassageIndex, setCurrentPassageIndex] = useState<number>(0);

  // Metadata form state
  const [subjects, setSubjects] = useState<ISelectElement[]>([]);
  const [domains, setDomains] = useState<ISelectElement[]>([]);
  const [standards, setStandards] = useState<ISelectElement[]>([]);
  const [states, setStates] = useState<ISelectElement[]>([]);
  const [selectedState, setSelectedState] = useState<ISelectElement | null>(
    null
  );
  const [selectedSubject, setSelectedSubject] = useState<ISelectElement | null>(
    null
  );
  const [selectedDomain, setSelectedDomain] = useState<ISelectElement | null>(
    null
  );
  const [selectedStandard, setSelectedStandard] =
    useState<ISelectElement | null>(null);
  const [selectedDok, setSelectedDok] = useState<IDokOption | null>(null);
  const [isPublished, setIsPublished] = useState<boolean>(false);
  const [cmsAuthorNotes, setCmsAuthorNotes] = useState<string>("");
  const [questionTypeName, setQuestionTypeName] = useState<string>("");
  const [questionTypes, setQuestionTypes] = useState<ISelectElement[]>([]);
  const [metadataLoading, setMetadataLoading] = useState<boolean>(true);

  // Save and Duplicate
  const [isDuplicateOpen, setIsDuplicateOpen] = useState<boolean>(false);
  const [duplicateQuestionType, setDuplicateQuestionType] =
    useState<ISelectElement | null>(null);
  const [duplicateNumberOfParts, setDuplicateNumberOfParts] =
    useState<ISelectElement | null>(null);
  const [duplicatePassageIdsInput, setDuplicatePassageIdsInput] =
    useState<string>("");
  const [isDuplicating, setIsDuplicating] = useState<boolean>(false);

  // Preview Item. The preview loads the saved Learnosity item, which only exists
  // once the question has been saved at least once.
  const [hasSavedItem, setHasSavedItem] = useState<boolean>(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  // Bumped when the preview closes so the editor re-initialises Learnosity Author
  // against its own container (the preview runs its own Author instance).
  const [editorInitNonce, setEditorInitNonce] = useState<number>(0);

  // Use a ref for the payload schema so it doesn't trigger editor re-initialization
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const payloadSchemaRef = useRef<any>(null);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const authorAppRef = useRef<any>(null);
  // Mirrors widgetDataArray synchronously so initialization callbacks can read the latest
  // in-memory edits without widgetDataArray being a useCallback dependency (which would
  // cause Learnosity to reinit on every edit and erase unsaved changes on part switches).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const widgetDataArrayRef = useRef<any[]>([]);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const duplicateDialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    setMetadataLoading(true);
    Promise.all([
      getTeacherQuestionById(teacherQuestionId).then((res) => res.data),
      getCmsTeacherSubjects(userId).then((res) => {
        const opts: ISelectElement[] = (res.data ?? []).map(
          (s: ICmsSubject) => ({
            value: String(s.subjectId),
            label: s.name,
          })
        );
        setSubjects(opts);
        return opts;
      }),
      getCmsQuestionTypes().then((res) => {
        const full: ICmsQuestionType[] = res.data ?? [];
        setQuestionTypes(
          full.map((qt: ICmsQuestionType) => ({
            value: String(qt.questionTypeId),
            label: qt.displayName ?? qt.name,
          }))
        );
        return full;
      }),
      getStateAll(9999, 1).then((res) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const opts: ISelectElement[] = ((res as any)?.data?.data ?? []).map(
          (s: { stateId: number; name: string }) => ({
            value: String(s.stateId),
            label: s.name,
          })
        );
        setStates(opts);
        return opts;
      }),
    ])
      .then(([q, subjectOpts, questionTypeFull, stateOpts]) => {
        setTeacherQuestion(q);
        setHasSavedItem(Boolean(q.questionReferenceId));

        const ids =
          routeState.passageIds && routeState.passageIds.length > 0
            ? routeState.passageIds
            : q.passageIds ?? [];
        setPassageIds(ids);
        setNumberOfParts(routeState.numberOfParts ?? 1);
        setIsPublished(q.isPublished ?? false);
        setCmsAuthorNotes(q.cmsAuthorNotes ?? "");

        // Pre-populate dropdowns
        const matchedState = stateOpts.find(
          (s) => s.value === String(q.stateId)
        );
        setSelectedState(matchedState ?? null);

        const matchedSubject = subjectOpts.find(
          (s) => s.value === String(q.subjectId)
        );
        setSelectedSubject(matchedSubject ?? null);

        const matchedQt = questionTypeFull.find(
          (qt) => qt.questionTypeId === q.questionTypeId
        );
        setQuestionTypeName(
          matchedQt
            ? matchedQt.displayName ?? matchedQt.name
            : String(q.questionTypeId)
        );

        if (matchedQt?.externalCode) {
          getTeacherQuestionTypePayload(matchedQt.externalCode)
            .then((r) => {
              payloadSchemaRef.current = r.data;
            })
            .catch(() => {
              // Fall back to default widget on error
            });
        }

        // Cascade: load domains for subject, then standards for domain
        if (q.subjectId) {
          getAllDomainsBySubject(q.subjectId).then((domainRes) => {
            const domainOpts: ISelectElement[] = (
              domainRes.data?.data ?? []
            ).map((d: { domainId: number; name: string }) => ({
              value: String(d.domainId),
              label: d.name,
            }));
            setDomains(domainOpts);
            const matchedDomain = domainOpts.find(
              (d) => d.value === String(q.domainId)
            );
            setSelectedDomain(matchedDomain ?? null);

            if (q.domainId) {
              getStandardOptionsByDomain(q.domainId).then((stdOpts) => {
                setStandards(stdOpts);
                setSelectedStandard(
                  stdOpts.find((s) => s.value === String(q.standardId)) ?? null
                );
              });
            }
          });
        }

        const matchedDok = dokOptions.find((d) => d.value === q.dok);
        setSelectedDok(matchedDok ?? null);
      })
      .finally(() => setMetadataLoading(false));
  }, [teacherQuestionId, userId]);

  useEffect(() => {
    if (!passageIds.length || !userInfo) {
      setPassageDetails([]);
      return;
    }
    Promise.all(
      passageIds.map((id) =>
        getTeacherPassageById(id, userInfo.districtId).then((r) => r.data)
      )
    ).then(setPassageDetails);
  }, [passageIds, userInfo]);

  const initialization = useCallback(async () => {
    if (!teacherQuestionId) return;
    setIsLoadingEditor(true);
    const itemRef = getTeacherItemRef(teacherQuestionId);
    const request = initRequest(itemRef, "questions");
    getRequest("author", JSON.stringify(request)).then(async (response) => {
      if (response.data && document.getElementById("learnosity-author")) {
        const callbacks = {
          readyListener: await async function () {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const app: any = authorAppRef.current;
            if (!app) return;

            app.on("render:item", async function () {
              const widgetRef = getPartFeatureRef(
                teacherQuestionId,
                currentPartIndex
              );
              const defaultWidget = payloadSchemaRef.current ?? {
                type: "mcq",
                stimulus: "",
              };

              // If we already loaded this part before (possibly with unsaved edits),
              // restore from the ref instead of overwriting with Learnosity's saved data.
              const inMemory = widgetDataArrayRef.current[currentPartIndex];
              if (inMemory !== undefined) {
                await app.setWidget(inMemory, widgetRef);
                setIsLoadingEditor(false);
                return;
              }

              // First load of this part — pull from Learnosity.
              const questions = app.getItem()?.questions ?? [];

              // Sync numberOfParts from Learnosity on the initial load so
              // the accordion renders all parts for a multi-part question.
              // The author session is scoped to this item, so questions.length
              // equals the total number of parts.
              if (currentPartIndex === 0 && questions.length > 1) {
                setNumberOfParts(questions.length);

                // Pre-populate all unvisited parts into the cache so they are
                // included in the save payload even if the user never clicks them.
                // Without this, widgetDataArray only has part 0, and save drops the rest.
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const prefillUpdates: Array<{ index: number; data: any }> = [];
                for (let i = 1; i < questions.length; i++) {
                  if (widgetDataArrayRef.current[i] !== undefined) continue;
                  const partRef = getPartFeatureRef(teacherQuestionId, i);
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  const partQuestion = questions.find(
                    (q: any) => q.reference === partRef
                  );
                  if (partQuestion) {
                    widgetDataArrayRef.current[i] = partQuestion.data;
                    prefillUpdates.push({ index: i, data: partQuestion.data });
                  }
                }
                if (prefillUpdates.length > 0) {
                  setWidgetDataArray((prev) => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const next: any[] = [...prev];
                    for (const { index, data } of prefillUpdates) {
                      next[index] = data;
                    }
                    return next;
                  });
                }
              }

              if (questions.length === 0) {
                await app.setWidget(defaultWidget, widgetRef);
                widgetDataArrayRef.current[currentPartIndex] = defaultWidget;
                setWidgetDataArray((prev) => {
                  const next = [...prev];
                  next[currentPartIndex] = defaultWidget;
                  return next;
                });
              } else {
                const existingQuestion = questions.find(
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (q: any) => q.reference === widgetRef
                );
                if (existingQuestion) {
                  const itemRef2 = app.getItem()?.item?.reference;
                  app.navigate(`items/${itemRef2}/widgets/${widgetRef}`);
                  widgetDataArrayRef.current[currentPartIndex] =
                    existingQuestion.data;
                  setWidgetDataArray((prev) => {
                    const next = [...prev];
                    next[currentPartIndex] = existingQuestion.data;
                    return next;
                  });
                } else {
                  await app.setWidget(defaultWidget, widgetRef);
                  widgetDataArrayRef.current[currentPartIndex] = defaultWidget;
                  setWidgetDataArray((prev) => {
                    const next = [...prev];
                    next[currentPartIndex] = defaultWidget;
                    return next;
                  });
                }
              }
              setIsLoadingEditor(false);
            });

            app.on("widgetedit:preview:changed", function () {
              const widget = app.getWidget();
              widgetDataArrayRef.current[currentPartIndex] = widget;
              setWidgetDataArray((prev) => {
                const next = [...prev];
                next[currentPartIndex] = widget;
                return next;
              });
            });
          },
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        authorAppRef.current = await (window as any)["LearnosityAuthor"]?.init(
          response.data,
          callbacks
        );
      }
    });
  }, [teacherQuestionId, currentPartIndex, editorInitNonce]);

  useEffect(() => {
    initialization();
  }, [initialization]);

  const handleSubjectChange = (option: ISelectElement | null) => {
    setSelectedSubject(option);
    setSelectedDomain(null);
    setSelectedStandard(null);
    setDomains([]);
    setStandards([]);
    if (option) {
      getAllDomainsBySubject(Number(option.value)).then((response) => {
        const opts: ISelectElement[] = (response.data?.data ?? []).map(
          (d: { domainId: number; name: string }) => ({
            value: String(d.domainId),
            label: d.name,
          })
        );
        setDomains(opts);
      });
    }
  };

  const handleDomainChange = (option: ISelectElement | null) => {
    setSelectedDomain(option);
    setSelectedStandard(null);
    setStandards([]);
    if (option) {
      getStandardOptionsByDomain(Number(option.value)).then(setStandards);
    }
  };

  const handleAddPart = () => {
    if (numberOfParts >= 4) return;
    const newIndex = numberOfParts;
    setNumberOfParts((n) => n + 1);
    setCurrentPartIndex(newIndex);
  };

  const handleRemovePart = (index: number) => {
    if (numberOfParts <= 1) return;
    widgetDataArrayRef.current = widgetDataArrayRef.current.filter(
      (_, i) => i !== index
    );
    setWidgetDataArray((prev) => {
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
    setNumberOfParts((n) => n - 1);
    setCurrentPartIndex((prev) => {
      if (prev > index) return prev - 1;
      if (prev === index && prev > 0) return prev - 1;
      return prev;
    });
  };

  const handleMovePart = (index: number, direction: "up" | "down") => {
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= numberOfParts) return;

    const refNext = [...widgetDataArrayRef.current];
    [refNext[index], refNext[targetIndex]] = [
      refNext[targetIndex],
      refNext[index],
    ];
    widgetDataArrayRef.current = refNext;
    setWidgetDataArray((prev) => {
      const next = [...prev];
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return next;
    });

    setCurrentPartIndex((prev) => {
      if (prev === index) return targetIndex;
      if (prev === targetIndex) return index;
      return prev;
    });
  };

  const handleRemovePassage = (index: number) => {
    setPassageIds((prev) => prev.filter((_, i) => i !== index));
    setPassageDetails((prev) => prev.filter((_, i) => i !== index));
    setCurrentPassageIndex((prev) => {
      if (prev > index) return prev - 1;
      if (prev === index && prev > 0) return prev - 1;
      return prev;
    });
  };

  const handleMovePassage = (index: number, direction: "up" | "down") => {
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= passageIds.length) return;

    setPassageIds((prev) => {
      const next = [...prev];
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return next;
    });
    setPassageDetails((prev) => {
      const next = [...prev];
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return next;
    });
    setCurrentPassageIndex((prev) => {
      if (prev === index) return targetIndex;
      if (prev === targetIndex) return index;
      return prev;
    });
  };

  /** Persists the current question. Returns true on success. Does not navigate. */
  const persistQuestion = async (): Promise<boolean> => {
    setIsSaving(true);
    try {
      const parts = widgetDataArray
        .map((widgetData, index) => ({
          partIndex: index,
          questionRequest: JSON.stringify(widgetData),
        }))
        .filter((p) => p.questionRequest !== "undefined");

      const request = {
        teacherQuestionId,
        questionText: widgetDataArray[0]?.stimulus ?? "",
        updatedByUserId: userInfo!.userId,
        parts,
        passageIds,
        stateId: Number(selectedState?.value ?? teacherQuestion?.stateId ?? 0),
        standardId: Number(
          selectedStandard?.value ?? teacherQuestion?.standardId ?? 0
        ),
        isPublished,
        dok: selectedDok?.value ?? teacherQuestion?.dok ?? 1,
        cmsAuthorNotes: cmsAuthorNotes || undefined,
      };
      await saveTeacherQuestion(request);

      // The editor stays open after a save, so keep the loaded snapshot in step
      // with what was just persisted rather than re-fetching (which would reset
      // the cascading selects and re-init Learnosity).
      setTeacherQuestion((prev) =>
        prev
          ? {
              ...prev,
              stateId: request.stateId,
              standardId: request.standardId,
              dok: request.dok,
              isPublished: request.isPublished,
              cmsAuthorNotes: request.cmsAuthorNotes ?? null,
              passageIds: request.passageIds,
            }
          : prev
      );
      setHasSavedItem(true);
      toast.success("Question saved successfully.");
      return true;
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: string } };
      toast.error(axiosError?.response?.data ?? "Failed to save question.");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  /** Saves and stays on the editor; persistQuestion reports the outcome. */
  const handleSave = async () => {
    await persistQuestion();
  };

  const canPreview = canPreviewTeacherItem(hasSavedItem, isSaving);

  const handleOpenPreview = () => {
    if (!canPreview) return;
    setIsPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setIsPreviewOpen(false);
    setEditorInitNonce((n) => n + 1);
  };

  const handleSaveAndDuplicate = async () => {
    if (!(await persistQuestion())) return;

    setDuplicateQuestionType(
      questionTypes.find(
        (qt) => qt.value === String(teacherQuestion?.questionTypeId)
      ) ?? null
    );
    setDuplicateNumberOfParts(
      numberOfPartsOptions.find((o) => o.value === String(numberOfParts)) ??
        null
    );
    setDuplicatePassageIdsInput(passageIds.join(", "));
    setIsDuplicateOpen(true);
    duplicateDialogRef.current?.showModal();
  };

  const handleCloseDuplicateDialog = () => {
    duplicateDialogRef.current?.close();
    setIsDuplicateOpen(false);
  };

  const handleDuplicateContinue = async () => {
    if (!userInfo || !duplicateQuestionType || !duplicateNumberOfParts) return;

    setIsDuplicating(true);
    const newPassageIds = parsePassageIds(duplicatePassageIdsInput);

    try {
      const request = buildDuplicateQuestionRequest({
        userId: Number(userId),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        cmsAuthorUserId: (store.getState() as any).profile?.userId || undefined,
        districtId: userInfo.districtId,
        stateId: Number(selectedState?.value ?? teacherQuestion?.stateId ?? 0),
        standardId: Number(
          selectedStandard?.value ?? teacherQuestion?.standardId ?? 0
        ),
        dok: selectedDok?.value ?? teacherQuestion?.dok ?? 1,
        questionTypeId: Number(duplicateQuestionType.value),
        passageIds: newPassageIds,
      });

      const response = await createTeacherQuestion(request);

      // Close before navigating - a native <dialog> lives in the top layer and
      // would otherwise stay open over the destination screen.
      handleCloseDuplicateDialog();
      navigate(`/crm/users/${userId}/content/questions/${response.data}`, {
        state: {
          numberOfParts: Number(duplicateNumberOfParts.value),
          passageIds: newPassageIds,
        },
      });
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: string } };
      toast.error(
        axiosError?.response?.data ??
          "Failed to duplicate question. Please try again."
      );
    } finally {
      setIsDuplicating(false);
    }
  };

  const handleOpenPassageDialog = () => {
    setPassageIdsInput(passageIds.join(", "));
    setIsEditingPassages(true);
    dialogRef.current?.showModal();
  };

  const handlePassageContinue = async () => {
    if (!userInfo) return;
    const newIds = parsePassageIds(passageIdsInput);

    setIsValidatingPassages(true);
    try {
      const fetched = await Promise.all(
        newIds.map((id) =>
          getTeacherPassageById(id, userInfo.districtId).then((r) => r.data)
        )
      );
      setPassageIds(newIds);
      setPassageDetails(fetched);
      dialogRef.current?.close();
      setIsEditingPassages(false);
    } catch {
      toast.error(
        "One or more passage IDs are invalid or do not belong to this district."
      );
    } finally {
      setIsValidatingPassages(false);
    }
  };

  const hasPassages = passageIds.length > 0;

  return (
    <>
      {/* Rendered ahead of the editor so its #learnosity-author container is the
          first in DOM order while the preview is open. */}
      {isPreviewOpen && (
        <PreviewModal
          show={isPreviewOpen}
          changeValue={(open: boolean) => {
            if (!open) handleClosePreview();
          }}
          itemRef={getTeacherItemRef(teacherQuestionId)}
          isTeacherQuestion={true}
          questionIdPreview={teacherQuestionId}
          teacherQuestionOwnerUserId={userId}
          questions={[
            {
              questionId: teacherQuestionId,
              englishQuestionReferenceId: getTeacherItemRef(teacherQuestionId),
            },
          ]}
          setQuestionIdPreview={() => undefined}
          setPreviewId={() => undefined}
          setQuestionToPreview={() => undefined}
        />
      )}
      <div className="flex-1 overflow-auto">
        <div className="h-auto min-h-half w-full px-8 py-6">
          {/* Editable metadata section */}
          <div className="border-b pb-6 mb-6">
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-3">
                <label className="text-sm text-gray-700">Question Type</label>
                <div className="mt-1 text-sm text-gray-900 py-2 px-3 bg-gray-100 rounded border border-gray-200 min-h-[38px]">
                  {questionTypeName ||
                    String(teacherQuestion?.questionTypeId ?? "")}
                </div>
              </div>

              <div className="col-span-3">
                <label className="text-sm text-gray-700">State</label>
                <Select
                  className="w-full mt-1 text-sm"
                  options={states}
                  onChange={(option) =>
                    setSelectedState(option as ISelectElement | null)
                  }
                  value={selectedState}
                  isClearable={true}
                  isSearchable={true}
                  theme={reactSelectTheme}
                  isLoading={metadataLoading}
                />
              </div>

              <div className="col-span-3">
                <label className="text-sm text-gray-700">Subject</label>
                <Select
                  className="w-full mt-1 text-sm"
                  options={subjects}
                  onChange={(option) =>
                    handleSubjectChange(option as ISelectElement | null)
                  }
                  value={selectedSubject}
                  isClearable={true}
                  isSearchable={true}
                  theme={reactSelectTheme}
                  isLoading={metadataLoading}
                />
              </div>

              <div className="col-span-3">
                <label className="text-sm text-gray-700">Domain</label>
                <Select
                  className="w-full mt-1 text-sm"
                  options={domains}
                  onChange={(option) =>
                    handleDomainChange(option as ISelectElement | null)
                  }
                  value={selectedDomain}
                  isClearable={true}
                  isSearchable={true}
                  isDisabled={!selectedSubject}
                  theme={reactSelectTheme}
                />
              </div>
            </div>

            <div className="grid grid-cols-12 gap-6 mt-5">
              <div className="col-span-3">
                <label className="text-sm text-gray-700">Standard</label>
                <Select
                  className="w-full mt-1 text-sm"
                  options={standards}
                  onChange={(option) =>
                    setSelectedStandard(option as ISelectElement | null)
                  }
                  value={selectedStandard}
                  isClearable={true}
                  isSearchable={true}
                  isDisabled={!selectedDomain}
                  theme={reactSelectTheme}
                />
              </div>

              <div className="col-span-3">
                <label className="text-sm text-gray-700">DOK</label>
                <Select
                  className="w-full mt-1 text-sm"
                  options={dokOptions}
                  onChange={(option) =>
                    setSelectedDok(option as IDokOption | null)
                  }
                  value={selectedDok}
                  isClearable={true}
                  isSearchable={true}
                  theme={reactSelectTheme}
                />
              </div>

              <div className="col-span-6 row-span-2 flex flex-col">
                <label className="text-sm text-gray-700">
                  CMS Author Notes{" "}
                  <span className="text-gray-400 font-light">(optional)</span>
                </label>
                <textarea
                  className="flex-1 w-full mt-1 text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-color resize-none"
                  value={cmsAuthorNotes}
                  onChange={(e) => setCmsAuthorNotes(e.target.value)}
                />
              </div>

              <div className="col-span-3 flex items-center">
                <input
                  id="isPublished"
                  name="isPublished"
                  type="checkbox"
                  checked={isPublished}
                  onChange={(e) => setIsPublished(e.target.checked)}
                  className="focus:ring-primary-color h-4 w-4 text-primary-color border-gray-300 rounded"
                />
                <label
                  htmlFor="isPublished"
                  className="ml-1.5 text-sm text-gray-700"
                >
                  Published ({publishedLabel(isPublished)})
                </label>
              </div>
            </div>
          </div>

          <div className="flex gap-6">
            {/* Passage panel */}
            {hasPassages && (
              <div className="w-1/3 flex-shrink-0">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    Passages
                  </span>
                  <button
                    type="button"
                    onClick={handleOpenPassageDialog}
                    className="text-xs text-primary-color hover:underline"
                  >
                    Edit Passages
                  </button>
                </div>
                <div className="space-y-2">
                  {passageDetails.map((pd, i) => {
                    const isExpanded = currentPassageIndex === i;
                    return (
                      <div
                        key={pd.teacherPassageId}
                        className="border rounded overflow-hidden"
                      >
                        <div
                          className={`flex items-center justify-between px-4 py-2 cursor-pointer select-none ${
                            isExpanded
                              ? "bg-primary-color text-white"
                              : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                          }`}
                          onClick={() => setCurrentPassageIndex(i)}
                        >
                          <span className="text-sm font-medium">
                            Passage {i + 1} (Id{" "}
                            <a
                              href={constant.crmUserContentPassageEdit
                                .replace(":userid", String(userId))
                                .replace(
                                  ":teacherPassageId",
                                  String(pd.teacherPassageId)
                                )}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className={`underline ${
                                isExpanded
                                  ? "text-white hover:text-white/80"
                                  : "text-primary-color hover:text-secondary-color"
                              }`}
                            >
                              {pd.teacherPassageId}
                            </a>
                            )
                          </span>
                          <div
                            className="flex items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              disabled={i === 0}
                              onClick={() => handleMovePassage(i, "up")}
                              title="Move up"
                              className={`text-sm px-1.5 py-0.5 rounded disabled:opacity-30 ${
                                isExpanded
                                  ? "hover:bg-white/20"
                                  : "hover:bg-gray-200"
                              }`}
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              disabled={i === passageIds.length - 1}
                              onClick={() => handleMovePassage(i, "down")}
                              title="Move down"
                              className={`text-sm px-1.5 py-0.5 rounded disabled:opacity-30 ${
                                isExpanded
                                  ? "hover:bg-white/20"
                                  : "hover:bg-gray-200"
                              }`}
                            >
                              ↓
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemovePassage(i)}
                              title="Remove passage"
                              className="text-sm px-1.5 py-0.5 rounded text-red-400 hover:text-red-600 hover:bg-red-50"
                            >
                              <DeleteIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="p-3">
                            <p className="text-sm font-semibold text-gray-800 mb-2">
                              {pd.title}
                            </p>
                            <div
                              className="text-sm text-gray-700 overflow-auto max-h-64"
                              dangerouslySetInnerHTML={{ __html: pd.passage }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Editor panel */}
            <div className={hasPassages ? "w-2/3" : "w-full"}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">
                    Questions
                  </span>
                  <span className="text-xs text-gray-300" aria-hidden="true">
                    |
                  </span>
                  <button
                    type="button"
                    onClick={handleOpenPreview}
                    disabled={!canPreview}
                    title={
                      hasSavedItem
                        ? "Preview the last saved version of this item"
                        : "Save the question to preview it"
                    }
                    className="text-xs text-primary-color hover:underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
                  >
                    Preview Item
                  </button>
                </div>
                {!hasPassages && (
                  <button
                    type="button"
                    onClick={handleOpenPassageDialog}
                    className="text-xs text-primary-color hover:underline"
                  >
                    Edit Passages
                  </button>
                )}
              </div>

              {/* Parts accordion */}
              <div className="space-y-2">
                {Array.from({ length: numberOfParts }, (_, i) => {
                  const isExpanded = currentPartIndex === i;
                  return (
                    <div key={i} className="border rounded overflow-hidden">
                      <div
                        className={`flex items-center justify-between px-4 py-2 cursor-pointer select-none ${
                          isExpanded
                            ? "bg-primary-color text-white"
                            : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                        }`}
                        onClick={() => setCurrentPartIndex(i)}
                      >
                        <span className="text-sm font-medium">
                          Part {i + 1}
                        </span>
                        {numberOfParts > 1 && (
                          <div
                            className="flex items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              disabled={i === 0}
                              onClick={() => handleMovePart(i, "up")}
                              title="Move up"
                              className={`text-sm px-1.5 py-0.5 rounded disabled:opacity-30 ${
                                isExpanded
                                  ? "hover:bg-white/20"
                                  : "hover:bg-gray-200"
                              }`}
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              disabled={i === numberOfParts - 1}
                              onClick={() => handleMovePart(i, "down")}
                              title="Move down"
                              className={`text-sm px-1.5 py-0.5 rounded disabled:opacity-30 ${
                                isExpanded
                                  ? "hover:bg-white/20"
                                  : "hover:bg-gray-200"
                              }`}
                            >
                              ↓
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemovePart(i)}
                              title="Remove part"
                              className="text-sm px-1.5 py-0.5 rounded text-red-400 hover:text-red-600 hover:bg-red-50"
                            >
                              <DeleteIcon className="h-5 w-5" />
                            </button>
                          </div>
                        )}
                      </div>

                      {isExpanded && (
                        // Keyed by editorInitNonce: opening the preview runs a second
                        // Learnosity Author instance, which swaps out this container's
                        // #learnosity-author node. Remounting gives the re-init a fresh
                        // container instead of React patching around a node it no
                        // longer owns.
                        <div className="p-2" key={editorInitNonce}>
                          {isLoadingEditor && (
                            <div className="text-sm text-gray-400 py-4 px-2">
                              Loading editor...
                            </div>
                          )}
                          <div id="learnosity-author" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {numberOfParts < 4 && (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={handleAddPart}
                    className="inline-flex items-center text-sm text-primary-color border border-primary-color rounded-lg py-1.5 px-4 hover:bg-primary-color hover:text-white transition-colors"
                  >
                    + Add Part
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Bottom action bar */}
          <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center text-sm rounded-lg text-gray-700 border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color py-1.5 px-4"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveAndDuplicate}
              disabled={isSaving || isDuplicating}
              className="inline-flex items-center justify-center text-sm rounded-lg text-primary-color border border-primary-color gap-2 bg-white hover:bg-primary-color hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color py-1.5 px-4 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? "Saving..." : "Save and Duplicate"}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving || isDuplicating}
              className="inline-flex items-center justify-center text-sm text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed min-w-[80px]"
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>

      {/* Edit Passages dialog */}
      <dialog
        ref={dialogRef}
        className="rounded-lg p-0 shadow-xl backdrop:bg-black backdrop:bg-opacity-50"
        onClose={() => setIsEditingPassages(false)}
      >
        {isEditingPassages && (
          <div className="p-6 min-w-96">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Edit Passages
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Enter the passage IDs associated with this question, separated by
              commas.
            </p>
            <div className="mb-6">
              <label className="text-sm text-gray-700">
                Passage IDs{" "}
                <span className="text-gray-400 font-light">
                  (comma-separated)
                </span>
              </label>
              <input
                type="text"
                className="w-full mt-1 text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-color"
                value={passageIdsInput}
                onChange={(e) => setPassageIdsInput(e.target.value)}
                placeholder="e.g. 79, 80"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handlePassageContinue}
                disabled={isValidatingPassages}
                className="inline-flex items-center text-sm text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isValidatingPassages ? "Validating..." : "Continue"}
              </button>
              <button
                type="button"
                onClick={() => {
                  dialogRef.current?.close();
                  setIsEditingPassages(false);
                }}
                className="inline-flex items-center text-sm rounded-lg text-gray-700 border border-gray-300 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color py-1.5 px-4"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </dialog>

      {/* Duplicate question dialog */}
      <dialog
        ref={duplicateDialogRef}
        className="rounded-lg p-0 shadow-xl backdrop:bg-black backdrop:bg-opacity-50"
        onClose={() => setIsDuplicateOpen(false)}
      >
        {isDuplicateOpen && (
          <div className="p-6 min-w-96">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Duplicate question
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              Choose the question type, number of parts, and any Passage Ids for
              the new question.
            </p>

            <div className="mb-4">
              <label className="text-sm text-gray-700">Question Type</label>
              <Select
                className="w-full mt-1 text-sm"
                options={questionTypes}
                onChange={(option) =>
                  setDuplicateQuestionType(option as ISelectElement | null)
                }
                value={duplicateQuestionType}
                isSearchable={true}
                theme={reactSelectTheme}
              />
            </div>

            <div className="mb-4">
              <label className="text-sm text-gray-700">Number of Parts</label>
              <Select
                className="w-full mt-1 text-sm"
                options={numberOfPartsOptions}
                onChange={(option) =>
                  setDuplicateNumberOfParts(option as ISelectElement | null)
                }
                value={duplicateNumberOfParts}
                theme={reactSelectTheme}
              />
            </div>

            <div className="mb-6">
              <label className="text-sm text-gray-700">
                Passage IDs{" "}
                <span className="text-gray-400 font-light">
                  (optional, comma-separated)
                </span>
              </label>
              <input
                type="text"
                className="w-full mt-1 text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-color"
                value={duplicatePassageIdsInput}
                onChange={(e) => setDuplicatePassageIdsInput(e.target.value)}
                placeholder="e.g. 79, 80"
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleDuplicateContinue}
                disabled={
                  isDuplicating ||
                  duplicateQuestionType === null ||
                  duplicateNumberOfParts === null
                }
                className="inline-flex items-center text-sm text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDuplicating ? "Creating..." : "Continue"}
              </button>
              <button
                type="button"
                onClick={handleCloseDuplicateDialog}
                className="inline-flex items-center text-sm rounded-lg text-gray-700 border border-gray-300 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color py-1.5 px-4"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </dialog>
    </>
  );
};

/**
 * Route wrapper that keys QuestionEditor on the question id. React Router
 * reuses the component instance when only a param changes, which would leave
 * the previous question's Learnosity refs (widget cache, author app) in place
 * when Save and Duplicate navigates question-to-question. Keying forces the
 * same fresh mount that arriving from /questions/new already produces.
 */
export const QuestionEditorRoute: React.FC = () => {
  const { teacherQuestionId } = useParams<{ teacherQuestionId: string }>();
  return <QuestionEditor key={teacherQuestionId} />;
};
